<template>
  <div>
    <card-dialog
      v-model="modalVisible"
      max-width="550px"
      :title="teamFormData.id ? $t('edit_team.dialog.header') : $t('new_team.dialog.header')"
    >
      <v-card-text
        class="pa-6"
      >
        <validation-observer ref="validation">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="teamFormData.name"
              :error-messages="errors"
              :label="$t('text_input.team_name.title')"
              dense
              outlined
              :placeholder="$t('text_input.team_name.placeholder')"
            />
          </validation-provider>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="teamFormData.shortName"
              :error-messages="errors"
              :label="$t('text_input.short_name.title')"
              dense
              outlined
              :placeholder="$t('text_input.short_name.placeholder')"
            />
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-select
              v-model="teamFormData.countryCode"
              name="team_id"
              :items="countries"
              item-value="code"
              item-text="name"
              :label="$t('dropdown.competition.title')"
              outlined
              dense
              :error-messages="errors"
            />
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-select
              v-model="teamFormData.rankId"
              name="team_id"
              :items="ranks.data"
              :loading="ranks.loading"
              item-value="id"
              item-text="name"
              :label="$t('text_input.data.sport_rank.label')"
              outlined
              dense
              :error-messages="errors"
              :placeholder="$t('text_input.data.sport_rank.placeholder')"
            />
          </validation-provider>
        </validation-observer>
        <div v-if="teamFormData.id">
          <v-row
            no-gutters
            class="mb-6"
          >
            <v-col>
              <div class="text-subtitle-1 grey--text text--darken-3">
                {{ $t('event_details.set_periods_event.player_settings') }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-switch
                v-model="teamFormData.leaveDefaultNotificationSettings"
                class="pa-0 ma-0"
                dense
                hide-details
                :label="$t('switch.leave_player_default')"
              />
            </v-col>
          </v-row>

          <div
            v-if="!teamFormData.leaveDefaultNotificationSettings"
            class="v-list--bordered mb-9"
          >
            <div class="red lighten-5 rounded-lg px-4 py-3 text-body-1 mb-6">
              {{ $t("warning.overwrite_player_settings") }}
            </div>
            <v-row
              v-if="hasPlayerTimezones"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="6"
                class="body-2"
              >
                {{ $t('dropdown.current_timezone.title') }}
              </v-col>
              <v-col>
                <v-autocomplete
                  v-model="teamFormData.notificationSettings.timezone"
                  outlined
                  :items="timezones"
                  item-value="tzCode"
                  item-text="name"
                  dense
                  hide-details
                  :placeholder="$t('dropdown.current_timezone.placeholder')"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasItlModule"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.receive_morning_itl') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.receive_itl_feedback"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasItlModule && teamFormData.notificationSettings.receive_itl_feedback"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.morning_itl_schedule') }}
              </v-col>
              <v-col>
                <v-btn-toggle
                  v-model="teamFormData.notificationSettings.itl_feedback_days"
                  dense
                  multiple
                  mandatory
                  color="primary"
                >
                  <v-btn
                    :value="1"
                    class="text-none"
                  >
                    {{ $t('day.mon') }}
                  </v-btn>
                  <v-btn
                    :value="2"
                    class="text-none"
                  >
                    {{ $t('day.tue') }}
                  </v-btn>
                  <v-btn
                    :value="3"
                    class="text-none"
                  >
                    {{ $t('day.wed') }}
                  </v-btn>
                  <v-btn
                    :value="4"
                    class="text-none"
                  >
                    {{ $t('day.thu') }}
                  </v-btn>
                  <v-btn
                    :value="5"
                    class="text-none"
                  >
                    {{ $t('day.fri') }}
                  </v-btn>
                  <v-btn
                    :value="6"
                    class="text-none"
                  >
                    {{ $t('day.sat') }}
                  </v-btn>
                  <v-btn
                    :value="7"
                    class="text-none"
                  >
                    {{ $t('day.sun') }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
            <v-row
              v-if="hasItlModule && teamFormData.notificationSettings.receive_itl_feedback"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('dropdown.morning_itl_time.title') }}
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="teamFormData.notificationSettings.itl_feedback_time"
                  outlined
                  dense
                  :items="[ '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00' ]"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasItlModule"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t("switch.disable_only_next_morning_itl") }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.disable_next_morning_feedback"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.receive_reminder') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.receive_event_reminder"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="teamFormData.notificationSettings.receive_event_reminder"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('dropdown.how_soon_before.title') }}
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="teamFormData.notificationSettings.event_reminder_minutes_before_event"
                  outlined
                  dense
                  :items="[ { text: $t('how_soon_settings.before.30_mins'), value: 30 }, { text: $t('how_soon_settings.before.1_hour'), value: 60 }, { text: $t('how_soon_settings.before.1.5_hours'), value: 90 }, { text: $t('how_soon_settings.before.2_hours'), value: 120 }, { text: $t('how_soon_settings.before.3_hours'), value: 180 }, { text: $t('how_soon_settings.before.4_hours'), value: 240 }]"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasItlModule"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.receive_rpe') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.receive_event_rpe_feedback"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasItlModule && teamFormData.notificationSettings.receive_event_rpe_feedback"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('dropdown.how_soon_after.title') }}
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="teamFormData.notificationSettings.rpe_feedback_minutes_after_event"
                  outlined
                  dense
                  :items="[ { text: $t('how_soon_settings.after.immediately'), value: 0 }, { text: $t('how_soon_settings.after.30_mins'), value: 30 }, { text: $t('how_soon_settings.after.1_hour'), value: 60 }, { text: $t('how_soon_settings.after.1.5_hours'), value: 90 }, { text: $t('how_soon_settings.after.2_hours'), value: 120 }, { text: $t('how_soon_settings.after.3_hours'), value: 180 }, { text: $t('how_soon_settings.after.4_hours'), value: 240 }]"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.allowed_to_create') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.create_events_allowed"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.allowed_to_reschedule') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.reschedule_events_allowed"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.receive_new_event') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.receive_new_event_notification"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.receive_new_media') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.receive_new_media_notification"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.receive_notes_updated') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.receive_event_note_updated_notification"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasTrainingBuilderModule"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.training_builder_settings.status_visibility') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.view_training_stats_allowed"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasGpsModule || hasHrModule"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.allowed_to_view_performance') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.view_performance_stats_allowed"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasDiagnosticsModule"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.allowed_to_view_diagnostics') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.view_diagnostic_stats_allowed"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasItlModule"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.allowed_to_view_wellness') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.view_wellness_stats_allowed"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="hasChatModule"
              align="center"
              class="v-list-item"
            >
              <v-col
                cols="8"
                class="body-2"
              >
                {{ $t('switch.messages_to_coaches') }}
              </v-col>
              <v-col>
                <v-switch
                  v-model="teamFormData.notificationSettings.chat_coach_allowed"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <v-row
          v-if="teamFormData.id"
          justify="center"
          align="center"
        >
          <v-col>
            <div class="text-subtitle-1 grey--text text--darken-3">
              {{ $t("player_performance.label") }}
            </div>
          </v-col>
          <v-col cols="auto">
            <v-btn
              outlined
              text
              small
              class="text-none"
              @click="performanceProfile = true"
            >
              {{ $t("button.manage") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <template v-slot:actions>
        <v-card-actions class="pa-4">
          <div class="flex-grow-1">
            <v-btn
              color="primary"
              block
              large
              :loading="submitting"
              @click="submit()"
            >
              {{ $t('training_builder.button.save') }}
            </v-btn>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="(teamFormData.playersCount > 0 || teamFormData.coachesCount > 0) ? on : undefined">
                  <v-btn
                    v-if="teamFormData.id"
                    color="error"
                    text
                    block
                    :disabled="teamFormData.playersCount > 0 || teamFormData.coachesCount > 0"
                    class="mt-1"
                    @click="toggleConfirmationDialog(true)"
                  >
                    {{ $t('button.delete') }}
                  </v-btn>
                </span>
              </template>
              <div>
                <div v-if="teamFormData.playersCount > 0">
                  {{ $t('button.delete.players.tooltip') }}
                </div>
                <div v-if="teamFormData.coachesCount > 0">
                  {{ $t('button.delete.coaches.tooltip') }}
                </div>
              </div>
            </v-tooltip>
          </div>
        </v-card-actions>
      </template>

      <delete-confirmation-dialog
        :visible.sync="deleteConfirmationDialogVisible"
        :submitting="deleting"
        @confirmed="deleteTeam()"
      />
    </card-dialog>

    <performance-profile-dialog
      v-if="teamFormData"
      :visible.sync="performanceProfile"
      :data="teamFormData"
      type="team"
      @save="handleSave"
    />
  </div>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { TeamApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { cloneDeep } from 'lodash'
import PerformanceProfileDialog from '@/components/players/PerformanceProfileDialog.vue'
import timezones from 'timezones-list'
import { SportApiService } from '@/data/services/SportApiService'
import { countries } from '@/data/models/Countries'

const emptySettings = {
  receive_itl_feedback: true,
  itl_feedback_days: [1, 2, 3, 4, 5, 6, 7],
  itl_feedback_time: '07:00',
  receive_event_rpe_feedback: true,
  rpe_feedback_minutes_after_event: 30,
  receive_event_reminder: true,
  event_reminder_minutes_before_event: 180,
  create_events_allowed: false,
  chat_coach_allowed: false,
  reschedule_events_allowed: false,
  view_performance_stats_allowed: false,
  view_diagnostic_stats_allowed: false,
  view_training_stats_allowed: true,
  view_wellness_stats_allowed: true,
  receive_new_event_notification: true,
  receive_new_media_notification: true,
  receive_event_note_updated_notification: true,
  disable_next_morning_feedback: false,
  timezone: null
}

export default {
  name: 'TeamFormDialog',
  components: { PerformanceProfileDialog, DeleteConfirmationDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    team: {
      type: Object,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      teamFormData: {
        id: undefined,
        name: '',
        shortName: ''
      },
      ranks: {
        loading: false,
        data: []
      },
      submitting: false,
      performanceProfile: false,
      managePerformanceProfile: false,
      countries: countries
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasChatModule() {
      return this.user.organisation_settings.has_chat_module
    },
    hasTrainingBuilderModule() {
      return this.user.organisation_settings.has_training_builder_module
    },
    hasDiagnosticsModule() {
      return this.user.organisation_settings.has_diagnostics_module
    },
    hasGpsModule() {
      return this.user.organisation_settings.has_gps_module
    },
    hasHrModule() {
      return this.user.organisation_settings.has_heart_rate_module
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module
    },
    hasPlayerTimezones() {
      return this.user.organisation_settings.has_player_timezones
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_TEAM_FORM, () => {
      this.modalVisible = true
      this.teamFormData = {
        id: undefined,
        name: '',
        shortName: '',
        leaveDefaultNotificationSettings: true,
        notificationSettings: cloneDeep(emptySettings)
      }

      this.fetchRanks()
    })

    EventBus.$on(this.$events.EDIT_TEAM, (team) => {
      this.teamFormData = {
        id: team.id,
        name: team.name,
        shortName: team.short_name,
        countryCode: team.country_code,
        rankId: team.rank_id,
        catapultId: team.catapult_id,
        playersCount: team.players_count,
        coachesCount: team.coaches_count,
        leaveDefaultNotificationSettings: true,
        notificationSettings: team.last_saved_settings,
        speed_profile: team.speed_profile,
        acceleration_profile: team.acceleration_profile,
        hr_profile: team.hr_profile
      }

      this.teamFormData.notificationSettings.disable_next_morning_feedback = false

      this.modalVisible = true

      this.fetchRanks()
    })

    this.timezones = [{ tzCode: null, name: 'Same as organisation' }, ...timezones]
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          name: this.teamFormData.name,
          short_name: this.teamFormData.shortName,
          country_code: this.teamFormData.id ? this.teamFormData.countryCode : undefined,
          rank_id: this.teamFormData.id ? this.teamFormData.rankId : undefined,
          catapult_id: this.teamFormData.catapultId,
          notification_settings: this.teamFormData.id ? this.teamFormData.notificationSettings : undefined,
          speed_profile: this.managePerformanceProfile ? this.teamFormData.speed_profile : undefined,
          acceleration_profile: this.managePerformanceProfile ? this.teamFormData.acceleration_profile : undefined,
          hr_profile: this.managePerformanceProfile ? this.teamFormData.hr_profile : undefined
        }

        let result

        if (this.teamFormData.id) {
          data.id = this.teamFormData.id

          result = await TeamApiService.update(data)
        } else {
          result = await TeamApiService.create(data)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.team_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.teamFormData.id) {
          EventBus.$emit(this.$events.TEAM_UPDATED, result.data)
        } else {
          EventBus.$emit(this.$events.TEAM_CREATED, result.data)
        }

        this.$store.dispatch(this.$storeActions.APP_FETCH_TEAMS)

        this.modalVisible = false
        this.managePerformanceProfile = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.team_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteTeam() {
      try {
        this.deleting = true

        await TeamApiService.delete(this.teamFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.team_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.TEAM_DELETED, this.teamFormData)

        this.modalVisible = false
      } catch (e) {
        if (e.response?.data?.error === 'team_not_empty') {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.move_all_players.error'),
            color: 'error'
          })
          return
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.team_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    handleSave(data) {
      this.managePerformanceProfile = true

      this.teamFormData.hr_profile = data.hr_profile
      this.teamFormData.speed_profile = data.speed_profile
      this.teamFormData.acceleration_profile = data.acceleration_profile

      this.save()
    },
    async fetchRanks() {
      try {
        this.ranks.loading = true

        this.ranks.data = await SportApiService.getAllRanks()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.ranks.loading = false
      }
    }
  }
}
</script>
